import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Header from "../../Header";
import Sidebar from "../../Sidebar";
import Footer from "../../Footer";
import Spinner from "../../common/Spinner";
import { createOrganization, Organizationlist } from "../../../redux/actions/OrganizationAction";
import { ORGANIZATION_DETAILS_SUCCESS } from "../../../redux/constants/OrganizationConstants";

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NAME_RE = /^[a-zA-Z0-9\s]*$/;
const PHONE_RE = /^\d*$/;

const CreateOrganization = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formState, setFormState] = useState({
        values: { name: "", email: "", phone: "", address: "" },
        errors: {},
    });

    const handleChange = (event) => {
        const { name, value } = event.target;

        if (name === "name" && !NAME_RE.test(value)) return;
        if (name === "phone" && !PHONE_RE.test(value)) return;

        const nextError = { ...formState.errors };
        if (name === "email") {
            nextError.email = value && !EMAIL_RE.test(value) ? "Invalid email format" : undefined;
        }

        setFormState((prev) => ({
            values: { ...prev.values, [name]: value },
            errors: nextError,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        const { name, email, phone, address } = formState.values;

        if (!name) return toast.error("Please enter a name");
        if (!email) return toast.error("Please enter an email");
        if (formState.errors.email) return toast.error(formState.errors.email);
        if (!phone) return toast.error("Please enter a phone number");
        if (!address) return toast.error("Please enter an address");

        setLoading(true);
        dispatch(createOrganization({ name, email, phone, address })).then(() => {
            setSubmitted(false);
            setLoading(false);
            dispatch(Organizationlist("", "", "", "", "Member")).then((response) => {
                if (response?.length > 0) {
                    dispatch({ type: ORGANIZATION_DETAILS_SUCCESS, payload: response[0] });
                }
            });
            history.push("/apps");
        });
    };

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="md" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, border: "1px solid", borderColor: "divider" }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                        <IconButton component={RouterLink} to="/apps" size="small" aria-label="Back">
                                            <ArrowBackIcon />
                                        </IconButton>
                                        <Typography variant="h3" component="h1">Create organization</Typography>
                                    </Stack>

                                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                                        Organizations group apps, builds, and members under a shared tenant. You can create more than one if you manage separate teams or clients.
                                    </Typography>

                                    <form onSubmit={handleSubmit} noValidate>
                                        <Stack spacing={3}>
                                            <TextField
                                                label="Organization name"
                                                name="name"
                                                required
                                                fullWidth
                                                autoFocus
                                                value={formState.values.name || ""}
                                                onChange={handleChange}
                                                error={submitted && !formState.values.name}
                                                helperText={submitted && !formState.values.name ? "Name is required" : " "}
                                            />
                                            <TextField
                                                label="Contact email"
                                                name="email"
                                                type="email"
                                                required
                                                fullWidth
                                                value={formState.values.email || ""}
                                                onChange={handleChange}
                                                error={Boolean((submitted && !formState.values.email) || formState.errors.email)}
                                                helperText={
                                                    (submitted && !formState.values.email && "Email is required") ||
                                                    formState.errors.email ||
                                                    " "
                                                }
                                            />
                                            <TextField
                                                label="Phone"
                                                name="phone"
                                                required
                                                fullWidth
                                                value={formState.values.phone || ""}
                                                onChange={handleChange}
                                                error={submitted && !formState.values.phone}
                                                helperText={submitted && !formState.values.phone ? "Phone is required" : " "}
                                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                            />
                                            <TextField
                                                label="Address"
                                                name="address"
                                                required
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={formState.values.address || ""}
                                                onChange={handleChange}
                                                error={submitted && !formState.values.address}
                                                helperText={submitted && !formState.values.address ? "Address is required" : " "}
                                            />

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                <Button type="submit" variant="contained" size="large" sx={{ minWidth: 180 }} disabled={loading}>
                                                    {loading ? "Creating…" : "Create organization"}
                                                </Button>
                                                <Button component={RouterLink} to="/apps" variant="outlined" size="large" sx={{ minWidth: 140 }}>
                                                    Cancel
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </form>
                                </Paper>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>
            {loading && <Spinner fullscreen label="Creating organization…" />}
        </>
    );
};

export default CreateOrganization;
